import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";

// Module-level cache for payroll params to avoid redundant network roundtrips across views
const paramsCache = new Map();

/**
 * Custom hook: payroll parameters aur branch-wise ESI/PF numbers padha.
 *
 * Parameters:
 *   firmIdOverride : number|string (optional) — specific firm Id, default is selectedFirm
 *
 * Returns:
 *   isMultiESI   : boolean  — MULTI ESI CODE parameter ON hai?
 *   isMultiPF    : boolean  — MULTI PF parameter ON hai?
 *   esiNoList    : string[] — Firm ki branches ke unique ESI numbers
 *   pfNoList     : string[] — Firm ki branches ke unique PF numbers
 *   branchList   : array    — Firm ki branches [{ Id, Name, PFNo, ESINo }]
 *   paramsLoaded : boolean  — Loading complete hui?
 */
const usePayrollParams = (firmIdOverride = null) => {
  const dispatch = useDispatch();

  const firmData = (() => {
    try { return JSON.parse(localStorage.getItem("selectedFirm") || "{}"); } catch { return {}; }
  })();
  const targetFirmId = firmIdOverride || firmData?.Id;
  const cached = targetFirmId ? paramsCache.get(targetFirmId) : null;

  const [isMultiESI,   setIsMultiESI]   = useState(cached ? cached.isMultiESI : false);
  const [isMultiPF,    setIsMultiPF]    = useState(cached ? cached.isMultiPF : false);
  const [esiNoList,    setEsiNoList]    = useState(cached ? cached.esiNoList : []);
  const [pfNoList,     setPfNoList]     = useState(cached ? cached.pfNoList : []);
  const [branchList,   setBranchList]   = useState(cached ? cached.branchList : []);
  const [paramsLoaded, setParamsLoaded] = useState(!!cached);

  const loadParams = useCallback(async () => {
    try {
      const firmData = JSON.parse(localStorage.getItem("selectedFirm") || "{}");
      const firmId = firmIdOverride || firmData?.Id;
      if (!firmId) {
        setParamsLoaded(true);
        return;
      }

      // If already cached, don't fire network requests repeatedly
      if (paramsCache.has(firmId)) {
        const c = paramsCache.get(firmId);
        setIsMultiESI(c.isMultiESI);
        setIsMultiPF(c.isMultiPF);
        setBranchList(c.branchList);
        setEsiNoList(c.esiNoList);
        setPfNoList(c.pfNoList);
        setParamsLoaded(true);
        return;
      }

      // 1. PayrollParameter fetch karo
      const params = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`
      );

      const p = params?.[0];
      const multiESI = !!p?.IsMultiESI;
      const multiPF  = !!p?.IsMultiPF;
      setIsMultiESI(multiESI);
      setIsMultiPF(multiPF);

      let bList = [];
      let eNos = [];
      let pNos = [];

      // 2. Agar MultiESI ya MultiPF ON hai to branches se ESI/PF numbers fetch karo
      if (multiESI || multiPF) {
        const branches = await Fn_FillListData(
          dispatch,
          () => {},
          "Data",
          `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`
        );

        if (branches && Array.isArray(branches)) {
          bList = branches;
          eNos = [
            ...new Set(
              branches
                .map(b => (b.ESINo || "").trim())
                .filter(v => v !== "")
            )
          ];
          pNos = [
            ...new Set(
              branches
                .map(b => (b.PFNo || "").trim())
                .filter(v => v !== "")
            )
          ];
        }
      }

      setBranchList(bList);
      setEsiNoList(eNos);
      setPfNoList(pNos);
      
      paramsCache.set(firmId, {
        isMultiESI: multiESI,
        isMultiPF: multiPF,
        branchList: bList,
        esiNoList: eNos,
        pfNoList: pNos
      });
    } catch (err) {
      console.error("usePayrollParams: Error loading params", err);
    } finally {
      setParamsLoaded(true);
    }
  }, [dispatch, firmIdOverride]);

  useEffect(() => {
    loadParams();
  }, [loadParams]);

  // Firm change hone par reload karo
  useEffect(() => {
    const onStorage = () => {
      paramsCache.clear();
      setParamsLoaded(false);
      loadParams();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, [loadParams]);

  return { isMultiESI, isMultiPF, esiNoList, pfNoList, branchList, paramsLoaded };
};

export default usePayrollParams;
